import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiFetch, descargarArchivo } from '../lib/api';
import { capInfo } from '../lib/capitulos';
import { Ayuda } from '../components/Ayuda';
import { AYUDA_PROCEDENCIA, ListaFuentes, componentesMarcas, conMarcas, type FuenteProcedencia } from '../lib/procedencia';

interface Borrador {
  id: string;
  capitulo: string;
  version: number;
  agent_code: string;
  estado: 'generando' | 'listo' | 'error' | string;
  error?: string | null;
  created_at: string;
  contenido?: string;
  fuentes?: FuenteProcedencia[] | null;
}

/**
 * Pestaña de PREVISUALIZACIÓN en vivo de un capítulo (2026-07-26). Sin sidebar: es una
 * vista-documento a pantalla completa que sigue SIEMPRE la última versión del borrador —
 * mientras Andrés edita (cada edición = rev+1), la preview se actualiza sola. La vista
 * renderiza el CONTENIDO fiel (markdown + figuras + tablas); el formato Word exacto se
 * obtiene con "Descargar Word".
 */
export default function PreviewCapitulo() {
  const { favoritoId = '', capitulo = '' } = useParams();
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recienActualizado, setRecienActualizado] = useState(false);
  const versionPrev = useRef<number | null>(null);
  const info = capInfo(capitulo);

  const refrescar = useCallback(async () => {
    try {
      const lista = await apiFetch<{ borradores: Borrador[] }>(`/api/dia/${favoritoId}/capitulos`);
      const ultimo = lista.borradores
        .filter((b) => b.capitulo === capitulo)
        .sort((a, b) => b.version - a.version)[0];
      if (!ultimo) { setBorrador(null); setError(null); return; }
      // Solo pedimos el contenido si cambió la versión (evita tráfico innecesario en el poll).
      setBorrador((actual) => {
        if (actual && actual.id === ultimo.id && actual.estado === ultimo.estado) return actual;
        void apiFetch<{ borrador: Borrador }>(`/api/dia/capitulos/${ultimo.id}`).then((d) => {
          setBorrador(d.borrador);
          if (versionPrev.current !== null && d.borrador.version > versionPrev.current) {
            setRecienActualizado(true);
            window.setTimeout(() => setRecienActualizado(false), 2500);
          }
          versionPrev.current = d.borrador.version;
        }).catch(() => undefined);
        return actual;
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el capítulo.');
    } finally {
      setCargando(false);
    }
  }, [favoritoId, capitulo]);

  useEffect(() => { void refrescar(); }, [refrescar]);
  // Poll en vivo: cada 4s revisa si hay una versión nueva (ediciones de Andrés).
  useEffect(() => {
    const t = window.setInterval(() => void refrescar(), 4000);
    return () => window.clearInterval(t);
  }, [refrescar]);
  useEffect(() => { document.title = `${info.titulo} · Vista previa · AMBEC`; }, [info.titulo]);

  return (
    <div className="min-h-dvh bg-[#EEF3F8]">
      {/* Barra superior fija: identidad del documento + estado en vivo + acciones */}
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200 bg-white/95 px-4 py-2.5 backdrop-blur sm:px-6">
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center text-[13.5px] font-semibold text-azul-noche">
            <span className="truncate">{info.titulo}</span>
            <Ayuda texto={AYUDA_PROCEDENCIA} />
          </p>
          <p className="text-[11.5px] font-normal text-gris">
            {borrador ? <>v{borrador.version} · {info.agente}</> : 'sin borrador aún'}
            {borrador?.estado === 'generando' && ' · redactando…'}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
            recienActualizado ? 'bg-emerald-100 text-emerald-700' : 'bg-celeste/20 text-azul-profundo'
          }`}
          title="Esta vista se actualiza sola cuando editas el capítulo"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${recienActualizado ? 'bg-emerald-500' : 'bg-azul-profundo/60 animate-pulse'}`} />
          {recienActualizado ? 'actualizado' : 'en vivo'}
        </span>
        <button
          type="button"
          onClick={() => borrador && void descargarArchivo(`/api/dia/capitulos/${borrador.id}/docx`, 'capitulo.docx')}
          disabled={!borrador || borrador.estado !== 'listo'}
          className="btn-secundario !h-8 !text-[12px] disabled:opacity-50"
          title="Word con el template oficial de AMBEC (figuras numeradas)"
        >
          Descargar Word
        </button>
      </header>

      <main className="px-3 py-6 sm:px-6 sm:py-10">
        {cargando && <p className="mx-auto max-w-[820px] text-center text-sm text-gris">Cargando…</p>}
        {error && <p role="alert" className="mx-auto max-w-[820px] rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        {!cargando && !error && !borrador && (
          <p className="mx-auto max-w-[820px] text-center text-sm text-gris">
            Todavía no hay un borrador de este capítulo. Genéralo desde el chat con Andrés y la vista aparecerá aquí.
          </p>
        )}
        {borrador && (
          <article className="mx-auto max-w-[820px] rounded-xl bg-white px-6 py-8 shadow-sm ring-1 ring-slate-200/70 sm:px-12 sm:py-14">
            {borrador.estado === 'generando' && (
              <p role="status" className="mb-5 rounded-lg bg-celeste/15 px-3 py-2 text-[13px] text-azul-noche">
                ✍️ {info.agente} está redactando este capítulo… se actualizará solo al terminar (suele tardar 2-3 min).
              </p>
            )}
            {borrador.estado === 'error' && (
              <p role="alert" className="mb-5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
                {borrador.error ?? 'La generación falló.'}
              </p>
            )}
            <div className="chat-md preview-doc">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ...componentesMarcas(borrador.fuentes),
                  img: ({ src, alt }) => (
                    <figure className="my-5 flex flex-col items-center gap-2">
                      <img src={typeof src === 'string' ? src : ''} alt={alt ?? ''} className="max-w-full rounded-md ring-1 ring-slate-200" />
                      {alt ? <figcaption className="text-center text-[12px] font-normal text-gris">{alt}</figcaption> : null}
                    </figure>
                  ),
                }}
              >
                {conMarcas(borrador.contenido)}
              </ReactMarkdown>
            </div>
            <ListaFuentes fuentes={borrador.fuentes} className="mt-8" />
          </article>
        )}
      </main>
    </div>
  );
}
